import { Fragment, useId, useState } from "react";
import Reveal from "../motion/Reveal";
import charlesUrl from "../assets/about/charles.png";
import haileyUrl from "../assets/about/hailey.png";
import sofiaUrl from "../assets/about/sofia.png";
import chrisUrl from "../assets/about/chris.png";
import { StrengthIcon, type StrengthIconName } from "./StrengthIcons";
import "./AboutPage.css";

const BIO = [
  "I like to joke that my main job as a designer is to keep people from throwing their laptops at walls.",
  "And while I'm fairly certain I've saved countless computers from an untimely demise, my approach to product design goes well beyond preventing frustration. I believe good design makes people feel confident and empowered, even when they're navigating something unfamiliar. The best design makes the experience enjoyable, too.",
  "I approach every design challenge knowing I'm not just responding to a ticket. I'm creating something that will matter to someone. Whether it's enterprise SaaS software people spend their workdays in or a website connecting people with their communities, what we build shapes how people navigate their day-to-day lives. I take seriously the responsibility to make those interactions better.",
  "That philosophy extends to how I lead. I thrive in complex environments where user needs, business priorities, and technical realities don't always neatly align. I ask the difficult questions, connect the dots across systems and teams, and help turn ambiguity into a clear direction forward. Because great design isn't just about better interfaces. It's about better decisions, better systems, and better outcomes for the people who rely on them.",
  "When I'm not designing, you'll usually find me armchair philosophizing, baking pies from scratch, tending to my ever-growing collection of orchids, feasting on potato tacos, or learning electric guitar.",
];

const STRENGTHS: { icon: StrengthIconName; title: string; body: string }[] = [
  {
    icon: "target",
    title: "Product Strategy & Problem Framing",
    body: "I uncover the problems worth solving, connecting user needs, business priorities, and technical realities to a clear product direction.",
  },
  {
    icon: "network",
    title: "Complex Systems Thinking",
    body: "I turn fragmented workflows and complicated enterprise platforms into cohesive, intuitive experiences.",
  },
  {
    icon: "brain",
    title: "Human-Centered AI Design",
    body: "I translate emerging AI capabilities into useful experiences that balance automation, transparency, trust, and user control.",
  },
  {
    icon: "pen-tool",
    title: "Hands-On Product Design",
    body: "I move fluidly from research and strategy to interaction design, prototyping, and detailed execution.",
  },
  {
    icon: "users-round",
    title: "Design Leadership & Influence",
    body: "I mentor designers, build strong partnerships, and align teams around decisions that move products forward.",
  },
  {
    icon: "blocks",
    title: "Design Systems & Operations",
    body: "I establish reusable foundations, shared standards, and practical processes that help design teams scale effectively.",
  },
];

const REFERENCES: {
  name: string;
  role: string;
  company: string;
  photo: string;
  pull: string;
  excerpt: string;
  tags: string[];
  body: string[];
}[] = [
  {
    name: "Charles Flinn",
    role: "Director of Product Operations",
    company: "Cadent",
    photo: charlesUrl,
    pull: "Arielle has a natural talent for design and UX that made her absolutely essential to every project she touched and oversaw.",
    excerpt:
      "Even when faced with seemingly impossible challenges, she always found a way to deliver high-quality work on time. The deep respect she earned from both her peers and her direct reports allowed her to successfully navigate tight deadlines…",
    tags: ["Design Leadership", "Execution", "Ownership"],
    body: [
      "“Her contributions and professional capabilities perfectly match her vibrant personality. Arielle has a natural talent for design and UX that made her absolutely essential to every project she touched and oversaw. Even when faced with seemingly impossible challenges, she always found a way to deliver high-quality work on time. The deep respect she earned from both her peers and her direct reports allowed her to successfully navigate tight deadlines, and it was always clear that her team would gladly go above and beyond for her.",
      "If you ever interact with Cadent's omnichannel platform, know that she contributed to just about everything you see there.”",
    ],
  },
  {
    name: "Hailey Melton",
    role: "Senior Product Designer",
    company: "Cadent",
    photo: haileyUrl,
    pull: "Arielle was the best manager I’ve ever had",
    excerpt:
      "In addition to being an empathetic leader, Arielle is also highly skilled in UX, research, and AI. She gives valuable feedback in design reviews and is great at translating complicated problems into simple, user-friendly designs…",
    tags: ["People Leadership", "UX & Research", "AI"],
    body: [
      "“Arielle was the best manager I’ve ever had, and I’m honored that we got to work together for 4 and a half years at AdTheorent and later Cadent. I felt supported and set up for success under her leadership.",
      "In addition to being an empathetic leader, Arielle is also highly skilled in UX, research, and AI. She gives valuable feedback in design reviews and is great at translating complicated problems into simple, user-friendly designs. Together, we redesigned a complex ad tech platform from the ground up.",
      "If you’re looking for a compassionate, collaborative, and very knowledgeable design leader, you can't go wrong with Arielle!”",
    ],
  },
  {
    name: "Sofia Poonawala",
    role: "Lead Product Designer",
    company: "Cadent",
    photo: sofiaUrl,
    pull: "Arielle's expertise in UX, data visualization, and AI is a tremendous asset, and I always valued her insightful guidance on complex projects.",
    excerpt:
      "She was a constant advocate for our team, ensuring we had the resources and support needed to grow. Arielle's strong leadership, design knowledge, and genuine care for her coworkers would make her a great addition to any team.",
    tags: ["Strategic Thinking", "Data & AI", "Team Advocacy"],
    body: [
      "“Grateful that I had the chance to work with Arielle as a design manager at Cadent.",
      "She is a compassionate and knowledgeable leader who was vital to our team's success. Arielle's expertise in UX, data visualization, and AI is a tremendous asset, and I always valued her insightful guidance on complex projects.",
      "She was a constant advocate for our team, ensuring we had the resources and support needed to grow. Arielle's strong leadership, design knowledge, and genuine care for her coworkers would make her a great addition to any team.”",
    ],
  },
  {
    name: "Chris Mechler",
    role: "Technical Support and Training Specialist",
    company: "MerusCase",
    photo: chrisUrl,
    pull: "She has an eye for design, a gift for writing, and a relentless desire to understand what makes people tick.",
    excerpt:
      "I had the pleasure of working alongside Arielle on the Marketing team at MerusCase. I was impressed by Arielle's ability to write great content and immerse herself in the legal sphere…",
    tags: ["Creativity", "Communication", "Research"],
    body: [
      "“Arielle is a fountain of creativity.\nShe's charismatic, thoughtful, hard-working, and kind. She has an eye for design, a gift for writing, and a relentless desire to understand what makes people tick.",
      "I had the pleasure of working alongside Arielle on the Marketing team at MerusCase. I was impressed by Arielle's ability to write great content and immerse herself in the legal sphere. She'll always put in the time and do the research to properly understand a topic, and she holds her own work to a very high standard.”",
    ],
  },
];

function ReferenceCard({
  person,
}: {
  person: (typeof REFERENCES)[number];
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <article className="reference-card panel">
      <div className="reference-identity">
        <img
          className="reference-portrait"
          src={person.photo}
          alt=""
          width={84}
          height={84}
        />
        <div className="reference-id-copy">
          <p className="reference-name">{person.name}</p>
          <p className="reference-role">{person.role}</p>
          <p className="reference-company">{person.company}</p>
        </div>
      </div>
      <span className="reference-divider" aria-hidden="true" />
      <div className="reference-content">
        <blockquote className="reference-pull">
          <span className="reference-mark" aria-hidden="true">
            “
          </span>
          <p>{person.pull}</p>
        </blockquote>
        <p className="reference-excerpt">{person.excerpt}</p>
        <div
          className={`reference-full${open ? " is-open" : ""}`}
          id={panelId}
          aria-hidden={open ? undefined : true}
        >
          <div className="reference-full-inner">
            {person.body.map((paragraph) => (
              <p key={paragraph.slice(0, 32)}>
                {paragraph.split("\n").map((line, index) => (
                  <Fragment key={line.slice(0, 24)}>
                    {index > 0 && <br />}
                    {line}
                  </Fragment>
                ))}
              </p>
            ))}
          </div>
        </div>
        <div className="reference-foot">
          <ul className="reference-tags">
            {person.tags.map((tag) => (
              <li key={tag} className="pill">{tag}</li>
            ))}
          </ul>
          <button
            type="button"
            className="reference-toggle"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => setOpen((value) => !value)}
          >
            <span className="reference-toggle-label">
              {open ? "Show less" : "Read full reference"}
            </span>
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </article>
  );
}

export default function AboutPage() {
  return (
    <div className="about-page" id="about">
      <main>
        <section className="about-hero" aria-labelledby="about-heading">
          <div className="about-intro">
            <Reveal>
              <div className="section-kicker">
                <span>02</span>
                <span className="section-rule section-rule-long" aria-hidden="true" />
              </div>
            </Reveal>
            <Reveal variant="editorial" delay={1}>
              <h1 id="about-heading" className="about-title">
                About
              </h1>
            </Reveal>
            <Reveal as="ul" variant="stagger" delay={2} className="about-roles">
              <li className="is-lead">Designer</li>
              <li>Advocate</li>
              <li>Storyteller</li>
              <li>Thinker</li>
            </Reveal>
            <Reveal variant="stagger" delay={3} className="about-bio">
              {BIO.map((paragraph) => (
                <p key={paragraph.slice(0, 24)}>{paragraph}</p>
              ))}
            </Reveal>
          </div>

          <section className="about-strengths" aria-labelledby="strengths-heading">
            <Reveal>
              <div className="section-kicker">
                <span>02.01</span>
                <span className="section-rule" aria-hidden="true" />
              </div>
            </Reveal>
            <Reveal variant="editorial" delay={1}>
              <h2 id="strengths-heading">Key strengths</h2>
            </Reveal>
            <div className="strength-list">
              {STRENGTHS.map((item, index) => {
                return (
                  <Reveal key={item.title} delay={index + 2}>
                    <article className="strength">
                      <StrengthIcon name={item.icon} />
                      <div className="strength-copy">
                        <h3>{item.title}</h3>
                        <p>{item.body}</p>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          </section>
        </section>

        <section className="about-references" aria-labelledby="references-heading">
          <Reveal>
            <div className="section-kicker">
              <span>02.02</span>
              <span className="section-rule" aria-hidden="true" />
            </div>
          </Reveal>
          <Reveal variant="editorial" delay={1}>
            <h2 id="references-heading">References</h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="references-subtitle">
              Kind words from people I've had the privilege to work with.
            </p>
          </Reveal>
          <div className="reference-row">
            {REFERENCES.map((person, index) => (
              <Reveal key={person.name} delay={index + 3}>
                <ReferenceCard person={person} />
              </Reveal>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
